/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { BrowserRouter } from 'react-router-dom'
import { AuthProvider } from '@/contexts/AuthContext'
import { PostHogProvider } from '@/contexts/PostHogContext'
import { ThemeProvider } from '@/components/theme/ThemeProvider'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Toaster } from '@/components/ui/sonner'
import App from './App.tsx'
import './index.css'

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
  // Session Replay
  replaysSessionSampleRate: 0.1, // 10% of sessions
  replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
  // Only enable in production or when explicitly set
  enabled: import.meta.env.PROD || import.meta.env.VITE_SENTRY_DSN !== undefined,
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes before marking as stale
      staleTime: 1000 * 60 * 5, // 5 minutes

      // Keep unused data in cache for 10 minutes before garbage collection
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)

      // Retry failed queries once with exponential backoff
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Don't refetch on window focus by default (reduces server load)
      // Individual queries can override this if needed
      refetchOnWindowFocus: false,

      // Don't refetch on mount if data is fresh
      refetchOnMount: false,

      // Refetch on network reconnect to ensure fresh data
      refetchOnReconnect: true,

      // Network mode: online-only (fail fast if offline)
      networkMode: 'online',
    },
    mutations: {
      // Retry mutations once on network errors
      retry: 1,
      retryDelay: 1000,

      // Network mode for mutations
      networkMode: 'online',
    },
  },
})

// Set document title from environment variable
document.title = import.meta.env.VITE_APP_NAME || 'AionTrack'

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <PostHogProvider>
            <AuthProvider>
              <ThemeProvider defaultTheme="system" storageKey="aiontrack-theme">
                <App />
                <Toaster />
              </ThemeProvider>
            </AuthProvider>
          </PostHogProvider>
        </BrowserRouter>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
)
